import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Loader, SectionHeading } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { fmtDate } from "../../lib/format";
import { Building2, Hammer, ShieldAlert, Wrench, Clock, UserCheck, CalendarCheck, CheckCircle2 } from "lucide-react";

function Metric({ label, value, icon: Icon, accent, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left bg-arq-card border border-border rounded-md p-5 hover:border-arq-green transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-arq-accent" : "text-arq-green"}`} strokeWidth={1.6} />
      </div>
      <p className="font-display text-4xl text-arq-ink mt-2">{value}</p>
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { data: m, isLoading } = useQuery({ queryKey: ["admin-metrics"], queryFn: async () => (await api.get("/admin/metrics")).data });
  if (isLoading || !m) return <Loader label="Loading dashboard" />;

  return (
    <div className="space-y-8 arq-fade-up">
      <SectionHeading overline="Operations" title="Admin Dashboard" />

      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Portfolio</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric label="Total Properties" value={m.total_properties} icon={Building2} onClick={() => navigate("/admin/properties")} testid="metric-total" />
          <Metric label="Under Construction" value={m.under_construction} icon={Hammer} onClick={() => navigate("/admin/properties?status=Under Construction")} testid="metric-construction" />
          <Metric label="Approaching Completion" value={m.approaching_completion} icon={CalendarCheck} accent testid="metric-approaching" onClick={() => navigate("/admin/properties")} />
          <Metric label="In Warranty" value={m.in_warranty} icon={ShieldAlert} testid="metric-warranty" onClick={() => navigate("/admin/properties")} />
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Service Operations</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric label="Open Requests" value={m.open_service_requests} icon={Wrench} onClick={() => navigate("/admin/service")} testid="metric-open" />
          <Metric label="Awaiting Review" value={m.awaiting_review} icon={Clock} accent onClick={() => navigate("/admin/service?status=Submitted")} testid="metric-review" />
          <Metric label="Awaiting Assignment" value={m.awaiting_assignment} icon={UserCheck} accent onClick={() => navigate("/admin/service?status=Approved")} testid="metric-assign" />
          <Metric label="Scheduled" value={m.scheduled_service} icon={CalendarCheck} onClick={() => navigate("/admin/service?status=Scheduled")} testid="metric-scheduled" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Metric label="New Requests" value={m.new_service_requests} icon={Wrench} accent onClick={() => navigate("/admin/service")} testid="metric-new" />
        <Metric label="Recently Completed" value={m.recently_completed} icon={CheckCircle2} onClick={() => navigate("/admin/service")} testid="metric-completed" />
        <Metric label="Warranties Expiring" value={m.expiring_warranties} icon={ShieldAlert} accent onClick={() => navigate("/admin/properties")} testid="metric-expiring" />
      </div>

      <div>
        <h2 className="font-display text-xl mb-4">Recent Service Requests</h2>
        <div className="bg-arq-card border border-border rounded-md divide-y divide-border">
          {m.recent_requests.length === 0 && <p className="p-6 text-sm text-muted-foreground">No requests yet.</p>}
          {m.recent_requests.map((s) => (
            <button key={s.id} onClick={() => navigate(`/admin/service/${s.id}`)} data-testid={`recent-sr-${s.id}`}
              className="w-full text-left flex items-center justify-between gap-4 p-4 hover:bg-arq-surface/40">
              <div><p className="text-sm font-medium text-arq-ink"><span className="font-mono">#{s.number}</span> · {s.category}</p><p className="text-xs text-muted-foreground">{s.homeowner_name} · {fmtDate(s.created_at)}</p></div>
              <StatusPill status={s.status} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
